/**
 * @fileoverview Hook para gerenciar contexto global e sincronização de dados.
 * Após sincronizar com o servidor, despacha actions para o Redux (globalData slice).
 *
 * @module useGlobalContext
 * @since 2.0.0
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import globalContextService from '../services/globalContextService.js';
import {
  setProducts,
  setCategories,
  setBrands,
  setPromotions,
  syncStarted,
  syncCompleted,
  syncFailed,
} from '../store/slices/globalDataSlice.js';

/**
 * Mapeia tipo de dado para a action Redux correspondente.
 */
const DATA_TYPE_ACTIONS = {
  products: setProducts,
  categories: setCategories,
  brands: setBrands,
  promotions: setPromotions,
};
 
/**
 * Hook para gerenciar contexto global e sincronização.
 * @returns {Object} Estado e funções do contexto global
 */
export function useGlobalContext() {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState(null);
  const autoSyncIntervalRef = useRef(null);

  /**
   * Carrega dados do localStorage para o Redux após sync.
   * @param {Array<string>} updatedTypes - Tipos de dados atualizados
   */
  const hydrateRedux = useCallback((updatedTypes) => {
    const allTypes = updatedTypes.length > 0
      ? updatedTypes
      : ['products', 'categories', 'brands', 'promotions'];

    // map: despacha action Redux para cada tipo de dado atualizado
    allTypes.forEach((type) => {
      const data = globalContextService.getLocalData(type);
      const action = DATA_TYPE_ACTIONS[type];
      if (data && action) {
        dispatch(action(data));
      }
    });
  }, [dispatch]);

  /**
   * Executa sincronização de dados.
   * @async
   * @returns {Promise<Object>} Resultado da sincronização
   */
  const syncData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    dispatch(syncStarted());

    try {
      const result = await globalContextService.syncData();
      setSyncResult(result);

      if (result.success || result.updated?.length > 0) {
        hydrateRedux(result.updated || []);
        dispatch(syncCompleted());
      } else if (!result.success) {
        const msg = result.error || 'Erro na sincronização';
        setError(msg);
        dispatch(syncFailed(msg));
      }

      return result;
    } catch (err) {
      const errorMessage = err.message || 'Erro desconhecido na sincronização';
      setError(errorMessage);
      dispatch(syncFailed(errorMessage));
      setSyncResult({
        success: false,
        error: errorMessage,
        updated: [],
        errors: [errorMessage],
      });
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, hydrateRedux]);

  /**
   * Inicializa o contexto global (executa sincronização inicial).
   * @async
   */
  const initialize = useCallback(async () => {
    if (isInitialized) return;

    console.log('🚀 Inicializando contexto global...');
    const result = await syncData();

    if (result.success || result.updated?.length > 0) {
      setIsInitialized(true);

      if (!autoSyncIntervalRef.current) {
        autoSyncIntervalRef.current = startAutoSync();
      }

      console.log('✅ Contexto global inicializado com sucesso');
    } else {
      console.warn('⚠️ Contexto global inicializado com erros:', result.errors);
      setIsInitialized(true);
    }
  }, [isInitialized, syncData]);

  /**
   * Inicia auto-sync que despacha para Redux após cada ciclo.
   * @returns {number} ID do intervalo
   */
  const startAutoSync = useCallback(() => {
    const SYNC_INTERVAL = 5 * 60 * 1000;
    console.log('🔄 Iniciando auto-sync a cada 5 minutos');

    return setInterval(async () => {
      try {
        const result = await globalContextService.autoSync();
        if (result.updated?.length > 0) {
          hydrateRedux(result.updated);
          dispatch(syncCompleted());
        }
      } catch (err) {
        console.error('❌ Erro no auto-sync interval:', err);
      }
    }, SYNC_INTERVAL);
  }, [dispatch, hydrateRedux]);

  /**
   * Obtém dados locais por tipo.
   * @param {string} dataType - Tipo de dados
   * @returns {Array|null} Dados locais
   */
  const getLocalData = useCallback((dataType) => {
    return globalContextService.getLocalData(dataType);
  }, []);

  /**
   * Verifica se há contexto local.
   * @returns {boolean}
   */
  const hasLocalContext = useCallback(() => {
    return globalContextService.getLocalContext() !== null;
  }, []);

  /**
   * Limpa todos os dados locais.
   */
  const clearAllData = useCallback(() => {
    if (autoSyncIntervalRef.current) {
      clearInterval(autoSyncIntervalRef.current);
      autoSyncIntervalRef.current = null;
    }

    globalContextService.clearAllData();
    dispatch(setProducts([]));
    dispatch(setCategories([]));
    dispatch(setBrands([]));
    dispatch(setPromotions([]));
    setSyncResult(null);
    setIsInitialized(false);
  }, [dispatch]);

  /**
   * Força uma nova sincronização.
   * @async
   */
  const forceSync = useCallback(async () => {
    console.log('🔄 Forçando nova sincronização...');
    return await syncData();
  }, [syncData]);

  // Cleanup do auto-sync quando componente desmonta
  useEffect(() => {
    return () => {
      if (autoSyncIntervalRef.current) {
        clearInterval(autoSyncIntervalRef.current);
      }
    };
  }, []);

  return {
    isLoading,
    isInitialized,
    syncResult,
    error,
    initialize,
    syncData,
    forceSync,
    getLocalData,
    hasLocalContext,
    clearAllData,
    isAutoSyncActive: !!autoSyncIntervalRef.current,
    hasError: !!error,
    isFirstLoad: syncResult?.isFirstLoad || false,
    updatedData: syncResult?.updated || [],
    errors: syncResult?.errors || [],
  };
}

export default useGlobalContext;
